import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, Box, Button, Tooltip, IconButton, Typography } from '@mui/material';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ScholarshipAgreementDialog from 'components/scholarshipPopup';
import useDialogToggle from 'pages/authentication/hooks/useDialogToggle';
import { Http } from 'api/http/http';
import { useDispatch } from 'react-redux';
import { logout } from 'store/reducers/auth';

const columns = [
  { id: 'process', label: '과정', minWidth: 100, align: 'center' },
  { id: 'moduleName', label: '모듈명', minWidth: 100, align: 'center' },
  { id: 'certificateNumber', label: '발급번호', minWidth: 100, align: 'center' },
  { id: 'date', label: '신청일자', minWidth: 100, align: 'center' },
  { id: 'certificateStatus', label: '상태', minWidth: 100, align: 'center' },
  { id: 'action', label: 'Action', minWidth: 100, align: 'center' }
];


export default function StudentCertificate() {
  const [rows, setRows] = useState([]);
  const [scholarshipStatus, setScholarshipStatus] = useState({}); // 장학금 신청 상태 저장
  const { open: openScholarshipDialog, handleClickOpen, handleClose: handleCloseDialog } = useDialogToggle();
  const [selectedRow, setSelectedRow] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleLogout = () => dispatch(logout());

  const handleRefreshExpired = () => {
    navigate('/studentLogin');
  };

  const http = new Http({ onRefreshExpired: handleRefreshExpired, onLogout: handleLogout });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 이수증과 장학금 신청 상태를 동시에 가져오기
        const [certificatesResponse, scholarshipsResponse] = await Promise.all([
          http.getWithToken('/api/certificates'),
          http.getWithToken('/api/scholarships')
        ]);

        const certificatesData = certificatesResponse.data.result;
        const scholarshipsData = scholarshipsResponse.data.result;

        // 장학금 신청 상태를 모듈명 기준으로 매핑
        const scholarshipStatusMap = {};
        scholarshipsData.forEach(scholarship => {
          scholarshipStatusMap[scholarship.moduleName] = scholarship.status;
        });
        setScholarshipStatus(scholarshipStatusMap);

        const data = certificatesData
          .map((item) => ({
            id: item.id,
            process: item.level,
            moduleName: item.moduleName,
            certificateNumber: item.issuedNumber,
            date: item.createdAt,
            certificateStatus: item.status,
            scholarshipApplied: scholarshipStatusMap[item.moduleName] === '신청중' || scholarshipStatusMap[item.moduleName] === '승인',
            isScholarshipAvailable: item.isScholarshipAvailable
          }))
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        // console.log(data);
        setRows(data);
      } catch (error) {
        console.error('데이터를 불러오는데 오류가 발생했습니다. 다시 시도해주세요');
      }
    };
    fetchData();
  }, []);

  // 장학금 신청 기간 확인 및 신청 처리
  const handleScholarshipClick = async (row) => {
    try {
      // 먼저 신청 기간인지 확인
      const periodResponse = await http.getWithToken('/api/scholarships/period');
      const isPeriodActive = periodResponse.data.result;

      if (!isPeriodActive) {
        alert('장학금 신청 기간이 아닙니다.');
        return;
      }

      // 신청 기간이 맞으면 재확인 팝업 띄우기
      setSelectedRow(row);
      handleClickOpen();
    } catch (error) {
      console.error('신청 기간 확인 중 오류 발생:', error);
      alert('신청 기간 확인에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }
  };

  // 장학금 버튼 클릭 후 닫을 때의 핸들러: 팝업의 확인 버튼 클릭하면 신청 완료(버튼 비활성화), 취소 버튼 클릭하면 신청x
  const handleClose = async (confirmed) => {
    if (confirmed) {
      try {
        // moduleName 대신 id를 사용하여 API 호출
        await http.postWithToken(`/api/scholarships/${selectedRow.id}`);
        setRows(rows.map((item) => (item.id === selectedRow.id ? { ...item, scholarshipApplied: true } : item)));
        alert('장학금 신청이 완료되었습니다.');
      } catch (error) {
        console.error('장학금 신청 중 오류 발생:', error);
        alert('장학금 신청에 실패했습니다. 잠시 후 다시 시도해주세요.');
      }
    }
    handleCloseDialog();
  };

  // 이수증 발급, 취소, 장학금 신청 버튼 클릭 시
  const handleClick = async (row, actionType) => {
    switch (actionType) {
      case 'issue':
        try {
          const responsePdf = await http.getWithToken(
            `/api/certificates/download?moduleName=${row.moduleName}`,
            {},
            { responseType: 'blob' }
          );
          const file = new Blob([responsePdf.data], { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL, '이수증 다운로드 하기', 'height=1000, width=800');
        } catch (error) {
          alert('이수증 pdf 다운로드 실패하였습니다. 잠시 후 다시 시도해주세요.');
        }
        break;

      case 'scholarship':
        // 장학금 신청 기간 확인 및 처리
        handleScholarshipClick(row);
        break;

      default:
      // console.log('Unknown action');
    }
  };

  // certificateStatus 따라 어떤 버튼이 action에 나타날 지
  const getActionButtons = (row) => {
    if (row.certificateStatus === 'PENDING') {
      return (
        <Box display="flex" justifyContent="center">
          <Typography variant="body2" color="textSecondary">
            심사 중
          </Typography>
        </Box>
      );
    } else if (row.certificateStatus !== 'PENDING') {
      if (row.certificateStatus === 'APPROVED') {
        // isScholarshipAvailable이 false인 경우 장학금 신청 불가
        if (!row.isScholarshipAvailable) {
          return (
            <Box display="flex" justifyContent="center">
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  fontStyle: 'italic'
                }}
              >
                장학금 수령 완료
              </Typography>
            </Box>
          );
        }

        // isScholarshipAvailable이 true인 경우에만 장학금 신청 버튼 표시
        const isScholarshipPending = scholarshipStatus[row.moduleName] === '신청중';
        const isScholarshipApproved = scholarshipStatus[row.moduleName] === '승인';

        return (
          <Box display="flex" justifyContent="center">
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={() => handleClick(row, 'scholarship')}
              disabled={row.scholarshipApplied || isScholarshipPending || isScholarshipApproved}
            >
              {isScholarshipPending ? '신청 중' :
                isScholarshipApproved ? '승인 완료' :
                  row.scholarshipApplied ? '신청 완료' : '장학금 신청'}
            </Button>
          </Box>
        );
      } else if (row.certificateStatus === 'REJECTED') {
        return (
          <Box display="flex" justifyContent="center">
            <Tooltip title="이수 조건 확인 후 교과목 이수현황 페이지에서 재신청해 주시기를 바랍니다.">
              <IconButton>
                <ErrorOutlineRoundedIcon style={{ color: 'red' }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      } else if (row.certificateStatus === 'CANCELED_BEFORE_APPROVAL') {
        return (
          <Box display="flex" justifyContent="center">
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                fontStyle: 'italic'
              }}
            >
              취소됨
            </Typography>
          </Box>
        );
      }
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Paper sx={{ width: '85%', overflow: 'hidden', p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              승인된 이수증의 발급은 총장 직인 후 종이 형태로 배부됩니다.
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 700 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead sx={{ backgroundColor: '#bfbfbf' }}>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell sx={{ textAlign: 'center' }} colSpan={columns.length}>
                      현재 목록이 비어있습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, index) => (
                    <TableRow hover role="checkbox" tabIndex={-1} key={`${index}-${row.moduleName}`}>
                      {columns.map((column) => {
                        let value;
                        if (column.id === 'certificateStatus') {
                          if (row[column.id]) {
                            if (row.certificateStatus === 'APPROVED') {  // APPROVAL에서 APPROVED로 변경
                              value = '승인';
                            } else if (row.certificateStatus === 'REJECTED') {  // REFUSAL에서 REJECTED로 변경
                              value = '반려';
                            } else if (row.certificateStatus === 'PENDING') {  // WAIT에서 PENDING으로 변경
                              value = '이수심사중';
                            } else if (row.certificateStatus === 'CANCELED_BEFORE_APPROVAL') {
                              value = '취소됨';
                            }
                          }
                        } else if (column.id === 'action') {
                          value = getActionButtons(row);
                        } else if (column.id === 'date') {
                          let date = new Date(row[column.id]);
                          let formattedDate =
                            date.getFullYear() +
                            '-' +
                            (date.getMonth() + 1).toString().padStart(2, '0') +
                            '-' +
                            date.getDate().toString().padStart(2, '0') +
                            ' ' +
                            date.getHours().toString().padStart(2, '0') +
                            ':' +
                            date.getMinutes().toString().padStart(2, '0') +
                            ':' +
                            date.getSeconds().toString().padStart(2, '0');

                          value = formattedDate;
                        } else if (column.id === 'process') {
                          if (row[column.id] === 'LOW') {
                            value = '초급';
                          } else if (row[column.id] === 'MIDDLE') {
                            value = '중급';
                          } else if (row[column.id] === 'HIGH') {
                            value = '고급';
                          } else {
                            value = row[column.id];
                          }
                        } else {
                          value = row[column.id];
                        }
                        return (
                          <TableCell key={`${row.moduleName}-${column.id}`} align={column.align}>
                            {value}
                          </TableCell>
                        );
                      })}

                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      <ScholarshipAgreementDialog open={openScholarshipDialog} handleClose={handleClose} />
      {/* CertificateCancelPopup 제거 */}
      {/* <CertificateCancelPopup open={openCancelDialog} handleClose={handleCancel} /> */}
    </>
  );
}
